import type { GameHistoryNoteResponse } from "libs/apiClient";
import { _ } from "i18n";
import { GameHistoryNoteEditForm } from "./GameHistoryNoteEditForm";
import { GameHistoryUserAvatar } from "./GameHistoryUserAvatar";
import type { GameHistoryPendingFile } from "./constants";
import { formatLogTimestamp } from "./formatLogTimestamp";

interface GameHistoryNoteCardProps {
	note: GameHistoryNoteResponse;
	isOwner: boolean;
	isEditing: boolean;
	editText: string;
	editDeletedFileIds: number[];
	editNewFiles: GameHistoryPendingFile[];
	isSaving: boolean;
	onEdit: () => void;
	onDelete: () => void;
	onEditTextChange: (text: string) => void;
	onRemoveExistingFile: (fileId: number) => void;
	onEditFileDrop: (files: File[]) => void;
	onRemoveNewFile: (fileId: string) => void;
	onEditSave: () => void;
	onEditCancel: () => void;
}

export function GameHistoryNoteCard({
	note,
	isOwner,
	isEditing,
	editText,
	editDeletedFileIds,
	editNewFiles,
	isSaving,
	onEdit,
	onDelete,
	onEditTextChange,
	onRemoveExistingFile,
	onEditFileDrop,
	onRemoveNewFile,
	onEditSave,
	onEditCancel,
}: GameHistoryNoteCardProps) {
	return (
		<div className="discoverGameHistory-noteCard">
			<GameHistoryUserAvatar
				photo={note.createdBy?.photo}
				className="discoverGameHistory-noteAvatar"
			/>
			<div className="discoverGameHistory-noteBody">
				<div className="discoverGameHistory-noteHeader">
					<span className="discoverGameHistory-noteAuthor">
						{note.createdBy?.name ?? _("discover.gameDetail.history.unknownUser")}
					</span>
					{isOwner && (
						<div className="discoverGameHistory-noteActions">
							<button
								type="button"
								className="discoverGameHistory-actionBtn"
								onClick={onEdit}
								disabled={isEditing}
							>
								<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M10.08 1.627a1.768 1.768 0 012.5 2.5L4.837 11.87l-3.17.797.796-3.17 7.617-7.87z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</button>
							<button
								type="button"
								className="discoverGameHistory-actionBtn discoverGameHistory-actionBtn--delete"
								onClick={onDelete}
							>
								<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M1.75 3.5h10.5M4.667 3.5V2.333a1.167 1.167 0 011.166-1.166h2.334a1.167 1.167 0 011.166 1.166V3.5m1.75 0v8.167a1.167 1.167 0 01-1.166 1.166H4.083a1.167 1.167 0 01-1.166-1.166V3.5h8.166z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</button>
						</div>
					)}
					<time className="discoverGameHistory-noteTime" dateTime={note.createdAt}>
						{formatLogTimestamp(note.createdAt)}
					</time>
				</div>
				{isEditing ? (
					<GameHistoryNoteEditForm
						editText={editText}
						existingFiles={note.files}
						editDeletedFileIds={editDeletedFileIds}
						editNewFiles={editNewFiles}
						isSaving={isSaving}
						onTextChange={onEditTextChange}
						onRemoveExistingFile={onRemoveExistingFile}
						onFileDrop={onEditFileDrop}
						onRemoveNewFile={onRemoveNewFile}
						onSave={onEditSave}
						onCancel={onEditCancel}
					/>
				) : (
					<>
						<p className="discoverGameHistory-noteText">{note.note}</p>
						{note.files.length > 0 && (
							<div className="discoverGameHistory-noteFiles">
								{note.files.map((file) => (
									<a
										key={file.id}
										href={file.url}
										target="_blank"
										rel="noopener noreferrer"
										download={file.filename}
										className="discoverGameHistory-fileLink"
									>
										{file.filename}
									</a>
								))}
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
